import React, { useState, useMemo, useEffect } from 'react'
import { Card } from "@/components/ui/Card";
import RHFInput from "@/components/form/RHFInput";
import { FormProvider, useForm } from "react-hook-form";
import { nameDefaultValue, nameSchema } from '../validation.schema';
import { useAuth } from '@/hooks/useAuth';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonShadcn as Button } from "@/components/ui/Button";
import toast from 'react-hot-toast';
import { AuthService } from '@/services/authService';
import { apiErrorMessageFormat } from '@/utils/formErrorUtils';

// Shared input styling for form fields
const INPUT_STYLE = "w-full rounded-md px-4 py-2.5 text-sm bg-background-secondary text-text-primary focus:outline-none border";

const AccountDetails = () => {

    // Get logged-in user data and refresh function
    const { user, currentUser } = useAuth();

    // Loading state while saving profile changes
    const [savingName, setSavingName] = useState(false);

    // Memoized initial form values from user data
    const initialUserData = useMemo(() => ({
        name: user?.name || "",
        email: user?.email || "",
    }), [user]);

    // React Hook Form setup with Zod validation
    const nameMethods = useForm({
        resolver: zodResolver(nameSchema),
        mode: "onSubmit",
        defaultValues: {
            ...nameDefaultValue,
            ...initialUserData
        },
    });

    // Extract form helpers
    const { handleSubmit: handleNameSubmit, reset: resetNameForm } = nameMethods;

    // Reset form whenever user data changes
    useEffect(() => {
        resetNameForm({
            ...nameDefaultValue,
            ...initialUserData
        });
    }, [initialUserData, resetNameForm]);

    // Handle profile name update
    const onSaveName = async (formData) => {
        try {
            setSavingName(true);

            // API call to update user name
            await AuthService.updateProfile({ name: formData.name });

            // Show success message
            toast.success("Profile updated");

            // Refresh latest user data
            currentUser();

            // Reset form state
            resetNameForm();

        } catch (e) {
            console.error(e);

            // Show error message
            toast.error(apiErrorMessageFormat(e, "Failed to update profile"));

        } finally {
            // Stop loading state
            setSavingName(false);
        }
    };

    return (
        <Card className="p-5">

            {/* Header section */}
            <div className="mb-4">
                <h2 className="text-sm font-semibold text-text-primary">
                    Account details
                </h2>

                <p className="text-xs text-text-secondary mt-1">
                    Update your display name.
                </p>
            </div>

            {/* Form provider for React Hook Form */}
            <FormProvider {...nameMethods}>

                {/* Account details form */}
                <form
                    onSubmit={handleNameSubmit(onSaveName)}
                    className="space-y-4"
                >

                    {/* Name input */}
                    <RHFInput
                        name="name"
                        label="Name"
                        placeholder="Enter your name"
                        inputClass={INPUT_STYLE}
                        required
                    />

                    {/* Read-only email input */}
                    <RHFInput
                        name="email"
                        label="Email"
                        placeholder="Enter your email"
                        inputClass={INPUT_STYLE}
                        readOnly
                    />

                    {/* Submit button */}
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={savingName}
                            className="rounded-xl px-5 py-2.5 bg-button-secondary text-text-base hover:bg-button-secondary/80"
                        >
                            {savingName ? "Saving..." : "Save name"}
                        </Button>
                    </div>

                </form>
            </FormProvider>
        </Card>
    )
}

export default AccountDetails
